import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken) {
        setToken(storedToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      }

      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser && parsedUser.avatar) {
            parsedUser.avatar = parsedUser.avatar.replace(/\\/g, '/');
          }
          setUser(parsedUser);
        } catch (error) {
          console.error('Error parsing stored user:', error);
          localStorage.removeItem('user');
        }
      }

      // Verify active session with backend (HttpOnly cookie is auto-sent with withCredentials: true)
      try {
        const res = await api.get('/auth/profile');
        const profileData = res.data?.data;
        if (profileData) {
          const photoDoc = (profileData?.onboardingDocuments || []).find(doc => doc.type === 'PHOTO');
          const resolvedAvatar = profileData?.avatar || photoDoc?.url;

          const resolvedAddress = profileData?.address || {
            street: profileData?.onboardingData?.currentAddress || '',
            city: profileData?.onboardingData?.city || '',
            state: profileData?.onboardingData?.state || '',
            pincode: profileData?.onboardingData?.pincode || '',
            country: 'India'
          };

          const baseUser = storedUser ? JSON.parse(storedUser) : {};
          const updated = {
            ...baseUser,
            ...profileData,
            avatar: resolvedAvatar || profileData.avatar,
            address: resolvedAddress
          };
          if (updated.avatar) {
            updated.avatar = updated.avatar.replace(/\\/g, '/');
          }
          localStorage.setItem('user', JSON.stringify(updated));
          setUser(updated);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          setToken(null);
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token: newToken, user: userData } = response.data.data;

      if (userData && userData.avatar) {
        userData.avatar = userData.avatar.replace(/\\/g, '/');
      }

      // 🛡️ Security: Store token in memory only, remove from localStorage
      localStorage.removeItem('token');
      localStorage.setItem('user', JSON.stringify(userData));

      setToken(newToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { token: newToken, user: newUser } = response.data.data;

      localStorage.removeItem('token');
      localStorage.setItem('user', JSON.stringify(newUser));

      setToken(newToken);
      setUser(newUser);

      return { success: true, user: newUser };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // ignore
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData);
      const updatedUser = response.data.data;

      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      return { success: true, user: updatedUser };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Profile update failed'
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.put('/auth/change-password', { currentPassword, newPassword });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Password change failed'
      };
    }
  };

  const refreshToken = async () => {
    try {
      const response = await api.post('/auth/refresh-token');
      const { token: newToken } = response.data.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      return { success: true };
    } catch (error) {
      logout();
      return { success: false };
    }
  };

  const updateUser = (userData) => {
    if (userData && userData.avatar) {
      userData.avatar = userData.avatar.replace(/\\/g, '/');
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    updateProfile,
    changePassword,
    refreshToken,
    isAuthenticated: !!user
  }), [user, token, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
